import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Spin,
  Tabs,
  TabsProps,
  Typography,
} from "antd";
import { useForm } from "antd/es/form/Form";
import PageHeading from "components/PageHeading.component";
import { ReadOnlyWhenRestricted } from "components/capability";
import { AdminCapability } from "shared/utils/capabilities";
import { DeleteClassModal } from "components/modals/DeleteClassModal.component";
import { useAuth } from "context/AuthContextProvider";
import {
  useDeleteClassType,
  useUpdateClassType,
} from "hooks/mutations/classtype";
import { getClassTypeByClassId } from "hooks/queries/classtype";
import { useTabParam } from "hooks/useTabParam";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClassType } from "shared/types";
import { ClassCategory } from "shared/types/enum";
import { CapacityField, CreditCostField, DurationField } from "./ClassFormFields";
import ClassTypePackage from "./ClassTypePackage";
import { textPx } from "shared/constants/zoom";

const ClassDetail = ({ classType }: { classType: ClassType }) => {
  const [form] = useForm();
  const { state } = useAuth();
  const navigate = useNavigate();
  const businessId = state.business?.id ?? "";
  const [isFieldChanged, setIsFieldChanged] = useState(false);
  const { mutate: updateClassType, isSuccess } = useUpdateClassType();
  const {
    mutate: deleteClassType,
    isSuccess: isDeleteSuccess,
    isPending: isDeletePending,
  } = useDeleteClassType();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const submitFieldChange = () => {
    setIsFieldChanged(true);
  };

  const handleSubmitChange = (values: any) => {
    updateClassType(
      {
        registerForm: { ...values, business_id: businessId },
        classId: classType.id,
      },
      {
        // A refused cost raise (a live tier can't pay it) leaves the stored
        // value in the field, not the number the server just refused.
        onError: () => form.setFieldsValue({ credit_cost: classType.credit_cost ?? 1 }),
      },
    );
  };

  // The pop-up asks the server first: upcoming sessions block the delete
  // (DeleteClassModal).
  const deleteClassTypeModal = () => setDeleteOpen(true);

  // Disable Update Button when the update is successful
  useEffect(() => {
    if (isSuccess) {
      setIsFieldChanged(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      navigate(PATHS.CLASS);
    }
    // eslint-disable-next-line
  }, [isDeleteSuccess]);

  return (
    <Card>
      <Typography.Title
        level={5}
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontSize: textPx(17),
          letterSpacing: "-0.01em",
        }}
      >
        {translate("title.information")} {translate("title.classes")}
      </Typography.Title>
      <Typography.Text
        style={{ display: "block", color: colors.ink2, fontSize: textPx(13), marginTop: 4 }}
      >
        {translate("subtitle.class_details")}
      </Typography.Text>
      <Form
        form={form}
        name="class-details"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmitChange}
        style={{ maxWidth: 640, marginTop: 20 }}
        initialValues={{
          name: classType.name,
          description: classType.description ?? undefined,
          max_participants: classType.max_participants,
          duration: classType.duration,
          type: classType.type,
          credit_cost: classType.credit_cost ?? 1,
        }}
        onFieldsChange={submitFieldChange}
      >
        <Form.Item
          name="name"
          label={translate("label.class_name")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.input")} ${translate(
                "label.class_name"
              )}`,
            },
          ]}
        >
          <Input
            placeholder={`${translate("form.placeholders.enter")} ${translate(
              "label.class_name"
            )}`}
          />
        </Form.Item>

        <Form.Item name="description" label={translate("label.description")}>
          <Input.TextArea
            placeholder={`${translate("form.placeholders.enter")} ${translate(
              "label.description"
            )}`}
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        </Form.Item>

        <Form.Item
          name="type"
          label={translate("label.class_type")}
          rules={[
            {
              required: true,
              message: `${translate("form.error.select")} ${translate(
                "label.class_type"
              )}`,
            },
          ]}
        >
          <Select disabled>
            {Object.entries(ClassCategory).map(([key, value]) => (
              <Select.Option key={key} value={value}>
                {key}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Jenis Kelas is fixed on edit, so the capacity field always shows
            this class's variant (max cap vs exact party size). */}
        <CapacityField classType={classType.type as ClassCategory} />
        <DurationField />
        <CreditCostField />

        <Divider style={{ margin: "20px 0" }} />
        <Flex gap={10}>
          {/* Button system: navy confirm, soft-red destructive. */}
            <Button type="primary" htmlType="submit" disabled={!isFieldChanged}>
              {translate("button.save_changes")}
            </Button>
          <Button
            color="danger"
            variant="filled"
            onClick={deleteClassTypeModal}
          >
            {translate("button.delete")} {translate("class")}
          </Button>
        </Flex>
      </Form>
      <DeleteClassModal
        open={deleteOpen}
        classId={classType.id}
        pending={isDeletePending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={(removeUpcoming, onRefused) =>
          deleteClassType({ classId: classType.id, removeUpcoming, onRefused })
        }
      />
    </Card>
  );
};

const ClassTypeDetails = () => {
  const navigate = useNavigate();
  const { classTypeId } = useParams();
  const { activeKey, onChange } = useTabParam("1", "tab", ["1", "2", "3"]);
  const {
    data: classType,
    isLoading,
    isError,
  } = getClassTypeByClassId(classTypeId ?? "");

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: translate("tabs.information"),
      // Pembatasan Admin: the form goes read-only (banner + disabled) for an
      // Admin whose Manager restricted the catalogue; viewing stays open.
      children: classType && (
        <ReadOnlyWhenRestricted capability={AdminCapability.CatalogPricing}>
          <ClassDetail classType={classType} />
        </ReadOnlyWhenRestricted>
      ),
    },
    {
      key: "2",
      label: translate("tabs.packages"),
      children: classType && (
        <ClassTypePackage classTypeId={classType.id} type="credit" />
      ),
    },
    {
      key: "3",
      label: translate("membership"),
      children: classType && (
        <ClassTypePackage classTypeId={classType.id} type="membership" />
      ),
    },
  ];

  // If retrieving is error throw to not found.
  useEffect(() => {
    if (isError) navigate(PATHS.NOT_FOUND);
    // eslint-disable-next-line
  }, [isError]);

  return (
    <>
      {isLoading ? (
        <Flex
          style={{
            justifyContent: "center",
            alignItems: "center",
            height: "100vh ",
          }}
        >
          <Spin />
        </Flex>
      ) : (
        <>
          <PageHeading title={translate("title.class_detail")} />
          <Tabs
            activeKey={activeKey}
            onChange={onChange}
            size="small"
            items={items}
          />
        </>
      )}
    </>
  );
};

export default ClassTypeDetails;
